import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import {
  FETCH_PAYMENT_TIMEOUT_MINUTES,
  PaymentMethodDataProps,
} from "_interfaces/_common/payment";
import { RechargeWalletProps } from "_interfaces/http-requests/recharge";
import { RechargeWalletModel } from "_models/data/recharge/data.rechargeWallet.model";
import { RechargeWalletErrorModel } from "_models/errors/recharge/error.rechargeWallet.model";
import InputText from "component/_common/forms/inputText";
import PaymentCheckout from "component/_common/payment/paymentCheckout";
import { paymentOptionsList } from "component/_common/payment/paymentOptionsList";
import useProcessStatusModal from "component/_common/status/useProcessStatusModal";
import { AppStatusCode } from "config/appStatusCode";
import { formatPriceWithDelimiter } from "functions/helper";
import { HTTP_ERROR } from "functions/http";
import {
  RechargeFulfill,
  RechargeWallet,
} from "functions/http-requests/recharge";
import { isValidAmount } from "functions/validators";
import RouteConstant from "navigation/constant";
import React, {
  ChangeEvent,
  FormEvent,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAlert } from "state/slice/alert";
import { toggleLoading } from "state/slice/loading";
import { validateWalletRecharge } from "./formValidator";

interface WalletRechargeProps {
  handleRefresh: () => void;
  handleClose: () => void;
}

const WalletRecharge: React.FC<WalletRechargeProps> = ({
  handleRefresh,
  handleClose,
}) => {
  const Dispatch = useDispatch();
  const navigate = useNavigate();

  const [state, setState] = useState<RechargeWalletModel>(
    new RechargeWalletModel(),
  );
  const [errors, setErrors] = useState<RechargeWalletErrorModel>(
    new RechargeWalletErrorModel(),
  );

  const [openPaymentOption, setOpenPaymentOption] = useState<boolean>(false);
  const [activeRechargeId, setActiveRechargeId] = useState<string>("");
  const [processingSnapshot, setProcessingSnapshot] = useState<{
    amount?: number;
    paymentBy?: string;
  }>({});
  const [processFailedMessage, setProcessFailedMessage] = useState<string>("");
  const DEFAULT_WALLET_RECHARGE_FAILED_MESSAGE =
    "We could not confirm this wallet top-up right now. Please wait a few minutes and try again. If you were charged, it may still update automatically.";

  const RECHARGE_POLL_SECONDS = 10;
  const RECHARGE_POLL_TIMEOUT_SECONDS = FETCH_PAYMENT_TIMEOUT_MINUTES * 60;

  const walletTopupProcessModal = useProcessStatusModal({
    timeoutSeconds: RECHARGE_POLL_TIMEOUT_SECONDS,
    processingGuardMessage:
      "Your wallet balance is being updated. Do not refresh, close, or go back.",
  });
  const {
    open: processOpen,
    status: processStatus,
    countdownLabel: processCountdownLabel,
    startProcessing: startProcessUi,
    setFailed: setProcessFailed,
    setDelayed: setProcessDelayed,
    close: closeProcessUi,
  } = walletTopupProcessModal;

  const resolveFailedMessage = useCallback(
    (message?: string) => message || DEFAULT_WALLET_RECHARGE_FAILED_MESSAGE,
    [],
  );

  const handleTogglePaymentOption = () =>
    setOpenPaymentOption(!openPaymentOption);

  const handleCloseAllWalletRechargeModals = useCallback(() => {
    closeProcessUi();
    setActiveRechargeId("");
    setProcessingSnapshot({});
    Dispatch(toggleLoading(false));
    setOpenPaymentOption(false);
    handleClose();
    setState(new RechargeWalletModel());
  }, [Dispatch, closeProcessUi, handleClose]);

  const walletRechargeDetails = useMemo(() => {
    return [
      { label: "Account", value: "Wallet" },
      {
        label: "Amount",
        value:
          typeof processingSnapshot.amount === "number"
            ? `UGX ${formatPriceWithDelimiter(processingSnapshot.amount)}`
            : "-",
      },
    ];
  }, [processingSnapshot.amount]);

  const walletRechargeEmbeddedProcess = useMemo(() => {
    if (!processOpen) return null;
    const paidWithWallet = processingSnapshot.paymentBy === "wallet";
    return {
      status: processStatus,
      processingTitle: "Adding wallet balance",
      successTitle: "Wallet balance updated",
      failedTitle: "Could not add wallet balance",
      delayedTitle: "Wallet balance update pending",
      processingMessage: paidWithWallet
        ? "Please do not press back, close this window, or refresh the page while we confirm your wallet payment and update your wallet balance."
        : "Please do not press back, close this window, or refresh the page while we confirm your payment and update your wallet balance.",
      successMessage: "Your wallet balance has been updated successfully.",
      failedMessage:
        processFailedMessage || DEFAULT_WALLET_RECHARGE_FAILED_MESSAGE,
      delayedMessage:
        "This wallet top-up is still being processed. Please wait up to 30 minutes; your wallet balance should update once the payment gateway confirms. If it still has not updated after 30 minutes, please contact support.",
      processingWarningMessage: paidWithWallet
        ? "Please do not press back, close this window, or refresh the page while we confirm your wallet payment and update your wallet balance."
        : "Please do not press back, close this window, or refresh the page while we confirm your payment and update your wallet balance.",
      details: walletRechargeDetails,
      onPrimaryAction:
        processStatus === "failed" || processStatus === "delayed"
          ? () => {
              handleCloseAllWalletRechargeModals();
              navigate(RouteConstant.CONTACT_US);
            }
          : undefined,
      primaryActionLabel: "Contact Support",
      onClose: handleCloseAllWalletRechargeModals,
    };
  }, [
    processOpen,
    processStatus,
    processFailedMessage,
    processingSnapshot.paymentBy,
    processCountdownLabel,
    walletRechargeDetails,
    DEFAULT_WALLET_RECHARGE_FAILED_MESSAGE,
    handleCloseAllWalletRechargeModals,
    navigate,
  ]);

  const handleFocus = (name: string) => {
    if (name) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target as HTMLInputElement;
    if (errors?.[name as keyof RechargeWalletErrorModel]) {
      setErrors((prev) => ({ ...prev, amount: "" }));
      return;
    }
    if (value && name === "amount" && !isValidAmount(value)) {
      setErrors((prev) => ({ ...prev, amount: "Invalid amount" }));
      return;
    }
    setState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  let handleRechargeRequest = (DATA: PaymentMethodDataProps) => {
    if (
      DATA?.paymentBy &&
      DATA?.paymentBy !== "wallet" &&
      DATA?.paymentMethod &&
      state?.amount
    ) {
      let PAYLOAD_DATA: RechargeWalletProps["DATA"] = {
        amount: Number(state.amount),
        paymentMethod: DATA.paymentMethod,
        paymentBy: DATA.paymentBy,
        momoPhoneNumber: DATA.momoPhoneNumber || undefined,
        airtelMOMOPhone: DATA.airtelMOMOPhone || undefined,
      };

      setProcessingSnapshot({
        amount: Number(state.amount || 0),
        paymentBy: DATA.paymentBy,
      });
      setProcessFailedMessage("");
      startProcessUi();
      setActiveRechargeId("");

      RechargeWallet({ DATA: PAYLOAD_DATA })
        .then((res) => {
          const data = res?.data;
          const rechargeId = data?.data?.rechargeId;

          if (data?.statusCode === AppStatusCode.api_success && rechargeId) {
            setActiveRechargeId(rechargeId);
          } else {
            setProcessFailedMessage(resolveFailedMessage(data?.message));
            setProcessFailed();
            Dispatch(
              setAlert({
                type: data?.level,
                message: data?.message || "Something went wrong!",
              }),
            );
          }
        })
        .catch((error) => {
          Dispatch(setAlert({ type: "error", message: HTTP_ERROR(error) }));
          setProcessFailedMessage(resolveFailedMessage(HTTP_ERROR(error)));
          setProcessFailed();
        });
    }
  };

  const handleCancelProcess = useCallback(() => {
    handleRefresh();
    handleCloseAllWalletRechargeModals();
  }, [handleRefresh, handleCloseAllWalletRechargeModals]);

  useEffect(() => {
    if (!processOpen || processStatus !== "processing" || !activeRechargeId)
      return;

    let intervalId: NodeJS.Timeout;
    let timeoutId: NodeJS.Timeout;
    let stopped = false;

    const stop = () => {
      stopped = true;
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };

    const poll = () => {
      if (stopped) return;

      RechargeFulfill(activeRechargeId)
        .then((res) => {
          const data = res?.data;

          if (data?.statusCode === AppStatusCode.api_success) {
            const STATUS = data?.data?.status?.toLowerCase();
            if (STATUS === "success") {
              stop();
              Dispatch(
                setAlert({
                  type: data?.level,
                  message: data?.message,
                }),
              );
              handleCancelProcess();
            } else if (STATUS === "pending") {
              // keep polling
            } else {
              setProcessFailedMessage(resolveFailedMessage(data?.message));
              setProcessFailed();
              stop();
            }
          } else {
            setProcessFailedMessage(resolveFailedMessage(data?.message));
            setProcessFailed();
            stop();
          }
        })
        .catch((error) => {
          setProcessFailedMessage(resolveFailedMessage(HTTP_ERROR(error)));
          setProcessFailed();
          stop();
        });
    };

    poll();
    intervalId = setInterval(poll, RECHARGE_POLL_SECONDS * 1000);
    timeoutId = setTimeout(() => {
      stop();
      setProcessDelayed();
    }, RECHARGE_POLL_TIMEOUT_SECONDS * 1000);

    return () => stop();
  }, [
    processOpen,
    processStatus,
    activeRechargeId,
    Dispatch,
    RECHARGE_POLL_TIMEOUT_SECONDS,
    setProcessFailed,
    setProcessDelayed,
    resolveFailedMessage,
    handleCancelProcess,
  ]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    let ValidateStep: {
      valid: boolean;
      errors: { name: string; error: string }[];
    } = validateWalletRecharge(state);

    if (!ValidateStep?.valid) {
      for (
        let i = 0, item: { name: string; error: string };
        !!(item = ValidateStep.errors[i++]);
      ) {
        setErrors((prevState) => ({ ...prevState, [item.name]: item.error }));
      }
      return;
    }

    handleTogglePaymentOption();
  };

  const inputList = [
    {
      type: "number",
      name: "amount",
      label: "Enter amount",
    },
  ];

  return (
    <>
      <DialogContent>
        <Box
          sx={{
            margin: "auto",
            textAlign: "center",
            maxWidth: 400,
            p: 3,
          }}
        >
          <Typography
            variant="h2"
            fontSize={36}
            fontWeight={700}
            textAlign="center"
            sx={{ textTransform: "capitalize" }}
          >
            Add Balance
          </Typography>

          <Typography>Enter amount</Typography>

          <Box pt={5}>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {inputList?.map((item, i) => (
                  <Fragment key={i}>
                    <Grid item xs={12}>
                      <InputText
                        type={item?.type}
                        label={item?.label}
                        name={item?.name}
                        value={state?.[item?.name as keyof RechargeWalletModel]}
                        error={
                          !!errors?.[
                            item?.name as keyof RechargeWalletErrorModel
                          ]
                        }
                        errorText={
                          errors?.[item?.name as keyof RechargeWalletErrorModel]
                        }
                        onChange={handleChange}
                        onFocus={() => handleFocus(item?.name)}
                      />
                    </Grid>
                  </Fragment>
                ))}

                <Grid item xs={12}>
                  <Box
                    pt={5}
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={2}
                  >
                    <Button
                      variant="text"
                      color="inherit"
                      onClick={handleClose}
                      sx={{
                        padding: "8px 20px",
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      sx={{
                        padding: "8px 20px",
                      }}
                    >
                      Continue
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* PAYMENT METHOD */}
      <Dialog open={openPaymentOption} maxWidth="md" fullWidth>
        <PaymentCheckout
          handleMakePaymentRequest={handleRechargeRequest}
          togglePaymentCheckout={handleTogglePaymentOption}
          paymentOptions={paymentOptionsList.filter(
            (item) => item.paymentBy !== "wallet",
          )}
          showOnlyPaymentMethod
          embeddedPaymentProcess={walletRechargeEmbeddedProcess}
        />
      </Dialog>
    </>
  );
};

export default WalletRecharge;
